import { useState, type ReactElement, type ReactNode } from 'react';
import { Box, Group, Text, type MantineBreakpoint } from '@mantine/core';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { type VizAccent } from '@/design/VizAccents';
import RankPagination from './RankPagination';

/** A trailing right-aligned secondary stat (e.g. reach, confidence) shown to the right of the bar. */
export interface RankedBarColumn<T> {
    /** Stable identity. */
    key: string;
    /** The formatted value (caller owns formatting). */
    render: (row: T) => ReactNode;
    /** The small dimmed caption beneath the value. */
    sublabel: (row: T) => ReactNode;
    /** Fixed column width in px. */
    width: number;
    /** Hide below this breakpoint (Mantine `visibleFrom`; defaults to `sm`). */
    visibleFrom?: MantineBreakpoint;
}

interface RankedBarListProps<T> {
    rows: T[];
    /** Stable React key per row. */
    rowKey: (row: T) => string;
    /** Row link target — every row is a drill-in link. */
    hrefFor: (row: T) => string;
    /** Accent hue for the share bar. */
    accent: VizAccent;
    /** Primary label text (truncates). */
    label: (row: T) => ReactNode;
    /** Bar fill width, 0–100 — typically the value's share of the peak row. */
    fill: (row: T) => number;
    /** Headline metric shown beside the label (already formatted). */
    primaryStat: (row: T) => ReactNode;
    /** Formatted share shown next to the headline metric (e.g. `42%`); omit to hide it. */
    share?: (row: T) => ReactNode;
    /** Trailing secondary stat columns. */
    columns?: RankedBarColumn<T>[];
    /** Rows per page before pagination kicks in; omit to show every row without pagination. */
    pageSize?: number;
    /** Plural noun for the pagination caption, e.g. `departments`. */
    unitPlural: string;
}

/**
 * A ranked list of horizontal share bars where every row is a drill-in link:
 * rank, label, a headline metric + share, a full-width share bar scaled to the
 * peak row, and optional trailing secondary stats. Purely presentational — the
 * caller supplies already-formatted values, so both the topic-aggregate
 * {@link RankedBarsViz} and the cost-breakdown TokenUsageCostRankedList are thin
 * adapters over it. When `pageSize` is set the list shows a top-N page at a time.
 */
export default function RankedBarList<T>({ rows, rowKey, hrefFor, label, fill, primaryStat, share, columns = [], pageSize, unitPlural }: RankedBarListProps<T>): ReactElement {
    const [page, setPage] = useState(1);
    const paginated = pageSize !== undefined && rows.length > pageSize;
    const pageCount = paginated && pageSize !== undefined ? Math.ceil(rows.length / pageSize) : 1;
    const safePage = Math.min(page, pageCount);
    const start = paginated && pageSize !== undefined ? (safePage - 1) * pageSize : 0;
    const visible = paginated && pageSize !== undefined ? rows.slice(start, start + pageSize) : rows;

    return (
        <Box>
            <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', background: 'var(--bm-color-card)', boxShadow: 'var(--bm-card-shadow)' }}>
                {visible.map((row, index) => (
                    <Box
                        key={rowKey(row)}
                        component={Link}
                        to={hrefFor(row)}
                        className="topic-drill-row"
                        style={{
                            display: 'block',
                            textDecoration: 'none',
                            color: 'inherit',
                            padding: '12px 16px',
                            borderBottom: index < visible.length - 1 ? '1px solid var(--bm-color-border-subtle)' : undefined,
                        }}
                    >
                        <Group gap="md" wrap="nowrap" align="center">
                            <Text ff="monospace" c="dimmed" fw={700} style={{ fontSize: '11px', width: 20, flexShrink: 0 }}>
                                {start + index + 1}
                            </Text>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                                <Group justify="space-between" gap="sm" mb={6} wrap="nowrap">
                                    <Text size="sm" fw={600} truncate style={{ minWidth: 0 }}>
                                        {label(row)}
                                    </Text>
                                    <Group gap={8} wrap="nowrap" style={{ flexShrink: 0 }}>
                                        <Text size="sm" ff="monospace" fw={700}>
                                            {primaryStat(row)}
                                        </Text>
                                        {share !== undefined && (
                                            <Text size="xs" ff="monospace" c="dimmed" style={{ width: 38, textAlign: 'right' }}>
                                                {share(row)}
                                            </Text>
                                        )}
                                    </Group>
                                </Group>
                                <Box style={{ height: 8, background: 'var(--bm-color-surface)' }}>
                                    <Box style={{ height: '100%', width: `${fill(row)}%`, background: 'var(--bm-color-brand-accent)' }} />
                                </Box>
                            </Box>
                            {columns.length > 0 && (
                                <Group gap="lg" wrap="nowrap" style={{ flexShrink: 0 }}>
                                    {columns.map((col) => (
                                        <Box key={col.key} style={{ textAlign: 'right', width: col.width }} visibleFrom={col.visibleFrom ?? 'sm'}>
                                            <Text size="xs" ff="monospace" fw={700}>
                                                {col.render(row)}
                                            </Text>
                                            <Text c="dimmed" style={{ fontSize: '9px' }}>
                                                {col.sublabel(row)}
                                            </Text>
                                        </Box>
                                    ))}
                                </Group>
                            )}
                            <ChevronRight size={15} color="var(--bm-color-text-dimmed)" style={{ flexShrink: 0 }} />
                        </Group>
                    </Box>
                ))}
            </Box>

            {paginated && <RankPagination start={start} pageSize={pageSize ?? 0} total={rows.length} pageCount={pageCount} page={safePage} onPageChange={setPage} unitPlural={unitPlural} />}
        </Box>
    );
}
